// components/UserSdgList.tsx

import React, { useEffect, useState } from 'react';
import { formatDistanceToNow, format, isYesterday } from 'date-fns';
import { Spinner } from 'react-bootstrap';
import DeleteConfirmation from 'common/components/ui/DeleteConfirmation';
import sdgs from '../data/sdgs.json'; // Import SDGs data
import { UserSDG } from 'sdgselection/types/SDG';
import SDGMiniItem from './SdgMiniItem';
import NoSdgs from './NoSdgs';

const SDG_DEFAULT = 13;

interface UserSdgListProps {
  sdg?: 'all' | number;              // for heading/context only (parent can pass filtered list)
  list?: UserSDG[];                  // pre-filtered list supplied by parent (like GlobalActionList)
  onEditClick?: (item: UserSDG) => void;
  onDeleteItem: (_id: string) => void;
  isLoading: boolean;
}

const formatFriendlyDate = (date?: Date | string): string => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isYesterday(d)) {
    return `Yesterday @ ${format(d, 'p')}`;
  }
  const diff = Date.now() - d.getTime();
  if (diff < 1000 * 60 * 60 * 24) {
    return formatDistanceToNow(d, { addSuffix: true });
  }
  return format(d, 'PPP @ p');
};

const UserSdgList: React.FC<UserSdgListProps> = ({
  sdg,
  list,
  onEditClick,
  onDeleteItem,
  isLoading,
}) => {
  const [items, setItems] = useState<UserSDG[]>([]);
  const [loading, setLoading] = useState(isLoading);
  const selectedSdg = sdg || SDG_DEFAULT;

  useEffect(() => {
    setItems(list || []);
    setLoading(false);
  }, [list]);

  return (
    <div className="mt-1">
      <h3>Sustainability Focus List {sdg && sdg !== 'all' ? `for SDG ${selectedSdg}` : ''} ( {items.length} )</h3>

      {loading ? (
        <Spinner animation="border" variant="primary" />
      ) : items.length === 0 ? (
            <NoSdgs />
      ) : (
        <div>
          {items.map((item, index) => (
            <div key={item._id} className="action-item row">
              <div
                className="clickable-content col-md-10"
                onClick={onEditClick ? () => onEditClick(item) : undefined}
                role={onEditClick ? 'button' : undefined}
              >
                <h6 className="mb-4">
                  {item.title || "No title"}{" "}
                  {item.description && (
                    <span className="text-muted">: {item.description}</span>
                  )}
                </h6>
                <div className="sdg-mini-list">
                    {sdgs.filter(sdg => item.sdgs.includes(sdg.id)).map((sdg, index: number) => (
                        <SDGMiniItem 
                            index={index}
                            key={`${item._id}-${sdg}`} 
                            sdg={sdg} 
                            onSelect={() =>{}} 
                            isSelected={true} 
                        />
                    ))}
                </div>    
              </div>

              <div className="action-controls col-md-2">
                <div className="row text-end">
                  
                  <div className="col-12 mb-2">
                    <DeleteConfirmation
                      index={index}
                      item={{ name: `${item.title || "this"}` }}
                      label="Remove"
                      buttonIsCustom={true}
                      onDelete={() => onDeleteItem(item._id)}
                    />
                  </div>

                  <div className="col-12 text-muted">
                    <small>
                      <strong>Created:</strong> 
                      <span className='friendly-date'>{formatFriendlyDate(item.created_at)}</span>
                      </small>
                      <br />
                    <small>
                      <strong>Updated:</strong> 
                      <span className='friendly-date'>{formatFriendlyDate(item.updated_at)}</span>
                    </small>
                  </div>

                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserSdgList;
